/**
 * Get the main error message from a Sentry event.
 */
export function getSentryEventMessage(event: any): string {
  return event?.exception?.values?.[0]?.value || event?.message || '';
}

/**
 * Get the last stack frame from the first exception in a Sentry event.
 * This is often the most useful frame for identifying noisy plugin/browser errors.
 */
export function getSentryLastFrame(event: any): any | null {
  const frames = event?.exception?.values?.[0]?.stacktrace?.frames || [];

  if (!Array.isArray(frames) || frames.length === 0) {
    return null;
  }

  return frames[frames.length - 1];
}

/**
 * Check whether the Sentry event came from a browser extension or similar external source.
 */
export function isBrowserExtensionNoise(event: any): boolean {
  const blob = JSON.stringify(event);

  return (
    blob.includes('app:///file:///Applications/') ||
    blob.includes('chrome-extension://') ||
    blob.includes('moz-extension://') ||
    blob.includes('safari-extension://')
  );
}

/**
 * Check whether the Sentry event is the expected Capacitor Camera cancel error.
 * This happens when a user opens photo selection and then cancels.
 */
export function isCameraCancelEvent(event: any): boolean {
  const message = getSentryEventMessage(event);
  const lastFrame = getSentryLastFrame(event);

  const frameFile = lastFrame?.filename || lastFrame?.module || '';
  const frameFunction = lastFrame?.function || '';

  return (
    message === 'User cancelled photos app' ||
    (
      message.includes('User cancelled photos app') &&
      frameFile.includes('camera')
    ) ||
    (
      frameFunction === 'HTMLInputElement.<anonymous>' &&
      message.includes('User cancelled photos app')
    )
  );
}

/**
 * Detect chunk load / MIME type errors (usually caused by deploy/cache mismatch).
 * Common in Safari when a JS file returns HTML instead.
 */
export function isChunkLoadMimeError(event: any): boolean {
  const message = getSentryEventMessage(event);
  const lastFrame = getSentryLastFrame(event);

  const frameFile = lastFrame?.filename || lastFrame?.module || '';

  return (
    // Safari MIME error
    message.includes('not a valid JavaScript MIME type') ||

    // Angular/Ionic runtime crash after bad chunk
    message.includes('isProxied') ||

    // Chrome error
    message.includes('Failed to fetch dynamically imported module') ||

    //safari version
    message.includes('Importing a module script failed') ||


    // Other variants
    message.includes('ChunkLoadError') ||
    message.includes('Loading chunk') ||

    // Safety: chunk file reference
    frameFile.includes('chunk-')
  );
}

//safari load errors
export function isUselessUnhandledRejection(event: any): boolean {
  const message = getSentryEventMessage(event);

  return (
    message.includes('CustomEvent') &&
    message.includes('unhandledrejection')
  );
}

// during dev with wasm errors
export function isLocalhostWasmFetchNoise(event: any): boolean {
  const blob = JSON.stringify(event);
  const message = getSentryEventMessage(event);
  const url = event?.request?.url || '';

  return (
    url.includes('http://localhost:8100') &&
    message.includes('Failed to fetch') &&
    (
      blob.includes('/assets/sql-wasm.wasm') ||
      blob.includes('jeep-sqlite') ||
      blob.includes('/@fs/') ||
      blob.includes('/.angular/cache/') ||
      blob.includes('[vite] server connection lost')
    )
  );
}
//app hang detection (often on iOS when the app is backgrounded for too long)
export function isAppHang(event: any): boolean {
  const message = getSentryEventMessage(event);

  return (
    message.includes('App hanging for at least 2000 ms')
  );
}

/**
 * Facebook in-app browser autofill bridge noise. JUNK!
 * Seen in Android FB IAB around login / form fields.
 */
export function isFacebookIabAutofillNoise(event: any): boolean {
  const message = getSentryEventMessage(event);
  const lastFrame = getSentryLastFrame(event);

  const frameFunction = lastFrame?.function || '';
  const blob = JSON.stringify(event);

  return (
    message.includes("Cannot read properties of undefined (reading 'value')") &&
    (
      frameFunction.includes('setContactAutofillValuesFromBridge') ||
      blob.includes('setContactAutofillValuesFromBridge') ||
      blob.includes('FB_IAB') ||
      blob.includes('FB4A') ||
      blob.includes('lm.facebook.com')
    )
  );
}

/**
 * Detect web SQLite initialisation noise from sql-wasm.
 * 
 * This occurs during DB bootstrap in web mode where the underlying
 * sqlite/jeep-sqlite layer throws a vague "SetInitialDBToStore:" error
 * - Safe to ignore unless frequency increases or correlates with DB issues
 */
export function isWebSqliteStoreInitNoise(event: any): boolean {
  const message = getSentryEventMessage(event);

  return (
    message.trim() === 'SetInitialDBToStore:' ||
    message.trim() === 'SetInitialDBToStore:'
  );
}

/**
 * Main helper used by Sentry.beforeSend().
 * Return true when the event is known noise and should be ignored.
 */
export function isIgnorableSentryEvent(event: any): boolean {
  // Ignore browser extension / external injected script noise.
  if (isBrowserExtensionNoise(event)) {
    return true;
  }

  // Ignore expected user cancel from the Capacitor Camera flow.
  if (isCameraCancelEvent(event)) {
    return true;
  }
  // Ignore chunk load / MIME errors (deploy/cache mismatch)
  if (isChunkLoadMimeError(event)) {
    return true;
  }
  //safari throws unhandledrejection errors that are often not actionable and pollute Sentry
  if (isUselessUnhandledRejection(event)) {
    return true;
  }

  if (isLocalhostWasmFetchNoise(event)) {
    return true;
  }

  if (isAppHang(event)) {
    return true;
  }

  if (isFacebookIabAutofillNoise(event)) {
    return true;
  }

  if (isWebSqliteStoreInitNoise(event)) {
    return true;
  }

  // Add future ignore rules here.
  // Example:
  // if (getSentryEventMessage(event).includes('ResizeObserver loop limit exceeded')) {
  //   return true;
  // }

  return false;
}

